// src/aiCharts.js
// Pure, tested helpers for charts in AI answers (no React in this file).
//
// Design rule: the AI only CHOOSES what to chart (a small spec). Every number
// comes from the owner's own job data, built here. Numbers the AI typed are
// never plotted.
//
// Spec (all keys other than these are ignored):
//   type:    "bar" | "line"             (line only works with data "monthly"; otherwise a bar chart is drawn)
//   data:    "monthly" | "jobs" | "clients" | "job_types"
//   measure: "revenue" | "costs" | "profit" | "margin"
//   from/to: "YYYY-MM", optional, whole months, inclusive
//   top:     whole number 1-20, optional, item charts only, default 10
//   title:   optional short text
//
// Money rules (same as the job summaries built in useContractorData in src/App.js):
//   revenue = invoices + manual revenue
//   costs   = purchase lines tagged to the job + manual expenses + labor
//   profit  = revenue - costs
//   margin  = profit / revenue as a percent (no value when revenue is 0)
// All sums are done in whole cents and converted to dollars only at the end,
// so 0.1 + 0.2 is exactly 0.30.

import { toWholeCents } from "./money";

export const CHART_TYPES = ["bar", "line"];
export const CHART_DATA = ["monthly", "jobs", "clients", "job_types"];
export const CHART_MEASURES = ["revenue", "costs", "profit", "margin"];
export const DEFAULT_TOP = 10;
export const MAX_TOP = 20;
export const MAX_MONTHS = 60;
export const CHART_FAILED_NOTE = "This chart couldn't be drawn from your data.";

const YM_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// ─── Spec ────────────────────────────────────────────────────────────────────

const cleanTitle = (t) => {
  if (typeof t !== "string") return "";
  // eslint-disable-next-line no-control-regex
  const s = t.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
  return s.length > 80 ? s.slice(0, 80).trim() : s;
};

// raw: the text inside the chart fence (or an already parsed object). Returns a clean spec or null.
export function parseChartSpec(raw) {
  let o = raw;
  if (typeof raw === "string") {
    try { o = JSON.parse(raw); } catch (e) { return null; }
  }
  if (!o || typeof o !== "object" || Array.isArray(o)) return null;
  if (!CHART_TYPES.includes(o.type) || !CHART_DATA.includes(o.data) || !CHART_MEASURES.includes(o.measure)) return null;

  const spec = {
    type: o.data === "monthly" ? o.type : "bar",
    data: o.data,
    measure: o.measure,
    title: cleanTitle(o.title),
  };
  for (const k of ["from", "to"]) {
    if (o[k] === undefined || o[k] === null || o[k] === "") continue;
    if (typeof o[k] !== "string" || !YM_RE.test(o[k])) return null;
    spec[k] = o[k];
  }
  if (spec.from && spec.to && spec.from > spec.to) return null;
  if (o.top !== undefined && o.top !== null) {
    if (typeof o.top !== "number" || !Number.isInteger(o.top) || o.top < 1 || o.top > MAX_TOP) return null;
    spec.top = o.top;
  }
  return spec;
}

// ─── Formatting ──────────────────────────────────────────────────────────────

// alwaysCents: show 2 decimals even when the cents are zero (used to keep a table column consistent).
export function formatMoney(dollars, alwaysCents = false) {
  if (typeof dollars !== "number" || !isFinite(dollars)) return "";
  const cents = toWholeCents(dollars);
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  const body = `$${whole.toLocaleString("en-US")}${frac || alwaysCents ? "." + String(frac).padStart(2, "0") : ""}`;
  return cents < 0 ? `-${body}` : body;
}

export function formatPercent(p) {
  if (typeof p !== "number" || !isFinite(p)) return "";
  return `${p}%`;
}

// Short axis label: $950, $12k, $1.5k, $2.4M, -$5k
export function formatAxisMoney(dollars) {
  if (typeof dollars !== "number" || !isFinite(dollars)) return "";
  const abs = Math.abs(dollars);
  const sign = dollars < 0 ? "-" : "";
  const trim = (n) => String(Math.round(n * 10) / 10).replace(/\.0$/, "");
  if (abs >= 1e6) return `${sign}$${trim(abs / 1e6)}M`;
  if (abs >= 1e3) return `${sign}$${trim(abs / 1e3)}k`;
  return `${sign}$${trim(abs)}`;
}

// ─── Data ────────────────────────────────────────────────────────────────────

export const toCents = (n) => (typeof n === "number" && isFinite(n) ? toWholeCents(n) : 0);

// "2025-03-14" or "2025-03-14T10:00:00Z" -> "2025-03". Anything else -> null. (Plain text, no time zones.)
function monthOf(d) {
  if (typeof d !== "string") return null;
  const m = d.match(/^(\d{4})-(0[1-9]|1[0-2])(?:-|T|$)/);
  return m ? `${m[1]}-${m[2]}` : null;
}

// "2025-03-14" or "2025-03-14T10:00:00Z" -> "2025-03-14". Anything else -> null. (Plain text, no time zones.)
export function dayOf(d) {
  if (typeof d !== "string") return null;
  const m = d.match(/^(\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01]))(?:T|$)/);
  return m ? m[1] : null;
}

const monthIndex = (ym) => parseInt(ym.slice(0, 4), 10) * 12 + (parseInt(ym.slice(5, 7), 10) - 1);
const monthFromIndex = (i) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
const shortMonth = (ym) => MONTH_NAMES[parseInt(ym.slice(5, 7), 10) - 1];
const longMonth = (ym) => `${MONTH_LONG[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(0, 4)}`;

// Every dated or undated money event for one job, in cents.
// src says where it came from: invoice, manualRev, purchase, purchaseLabor, manualExp (materials), manualExpLabor (a cash cost that counts as labor), labor, or other (money in the job's
// own totals with no date, for example costs assigned from the inbox). day is "YYYY-MM-DD" or null.
export function jobEvents(job) {
  const ev = [];
  const add = (kind, src, amount, date) => ev.push({ kind, src, cents: toCents(amount), ym: monthOf(date), day: dayOf(date) });
  (job.invoices || []).forEach((i) => add("rev", "invoice", i.TotalAmt, i.TxnDate));
  (job.manualRevenue || []).forEach((r) => add("rev", "manualRev", r.amount, r.revenueDate));
  (job.purchases || []).forEach((p) => {
    (p.Line || []).forEach((l) => {
      if (l && l.AccountBasedExpenseLineDetail?.CustomerRef?.value === job.id) add("cost", l.isLabor === true ? "purchaseLabor" : "purchase", l.Amount, p.TxnDate);
    });
  });
  (job.manualExpenses || []).forEach((e) => add("cost", e.isLabor === true ? "manualExpLabor" : "manualExp", e.amount, e.expenseDate));
  (job.laborEntries || []).forEach((l) => add("cost", "labor", l.amount, l.workDate));
  // Anything in the job's own totals that has no date (for example costs assigned from the inbox).
  for (const [kind, total] of [["rev", job.revenue], ["cost", job.costs]]) {
    if (typeof total !== "number" || !isFinite(total)) continue;
    const have = ev.filter((e) => e.kind === kind).reduce((s, e) => s + e.cents, 0);
    const rest = toCents(total) - have;
    if (rest !== 0) ev.push({ kind, src: "other", cents: rest, ym: null, day: null });
  }
  return ev;
}

// One place that sorts every money event into months (plain date text, whole cents).
// inRange decides which months to keep. Undated money is totalled separately.
function bucketByMonth(jobs, inRange) {
  const buckets = new Map(); // ym -> {rev, cost}
  let undatedCostCents = 0;
  let undatedRevCents = 0;
  for (const job of jobs) {
    if (!job || typeof job !== "object") continue;
    for (const e of jobEvents(job)) {
      if (e.ym === null) { if (e.kind === "cost") undatedCostCents += e.cents; else undatedRevCents += e.cents; continue; }
      if (!inRange(e.ym)) continue;
      const b = buckets.get(e.ym) || { rev: 0, cost: 0 };
      if (e.kind === "rev") b.rev += e.cents; else b.cost += e.cents;
      buckets.set(e.ym, b);
    }
  }
  return { buckets, undatedCostCents, undatedRevCents };
}

// Monthly totals for the AI's data. Same numbers as the "monthly" chart with no date range:
// [{ month: "2026-03", revenue, costs, profit }] in dollars, sorted by month, only months with activity.
export function monthlyTotalsForAI(jobs) {
  if (!Array.isArray(jobs)) return [];
  const { buckets } = bucketByMonth(jobs, () => true);
  return [...buckets.keys()].sort().map((month) => {
    const b = buckets.get(month);
    return { month, revenue: b.rev / 100, costs: b.cost / 100, profit: (b.rev - b.cost) / 100 };
  });
}

// Undated money in whole cents, for notes next to monthly totals.
export function undatedTotals(jobs) {
  if (!Array.isArray(jobs)) return { costCents: 0, revCents: 0 };
  const { undatedCostCents, undatedRevCents } = bucketByMonth(jobs, () => true);
  return { costCents: undatedCostCents, revCents: undatedRevCents };
}

// A short line telling the AI what the monthly totals leave out. Empty text when nothing is left out.
export function undatedNoteForAI(jobs) {
  if (!Array.isArray(jobs)) return "";
  const { undatedCostCents: c, undatedRevCents: r } = bucketByMonth(jobs, () => true);
  const parts = [];
  if (c !== 0) parts.push(`${formatMoney(c / 100)} of costs`);
  if (r !== 0) parts.push(`${formatMoney(r / 100)} of revenue`);
  const verb = c === 0 ? "has" : "have"; // "revenue that has no date", but "costs that have no date"
  return parts.length ? `Monthly totals leave out ${parts.join(" and ")} that ${verb} no date.` : "";
}

// Percent with one decimal, rounded half away from zero. Null unless revenue is above 0 (same as the dashboard).
export function marginPercent(profitCents, revenueCents) {
  if (!(revenueCents > 0)) return null;
  const x = (Math.abs(profitCents) * 1000) / Math.abs(revenueCents);
  const r = Math.round(x) / 10;
  const neg = (profitCents < 0) !== (revenueCents < 0);
  return neg && r !== 0 ? -r : r;
}

function makeRow(key, label, fullLabel, revenueCents, costsCents) {
  const profitCents = revenueCents - costsCents;
  return { key, label, fullLabel, revenueCents, costsCents, profitCents, margin: marginPercent(profitCents, revenueCents) };
}

function valueOf(row, measure) {
  if (measure === "margin") return row.margin;
  const c = measure === "revenue" ? row.revenueCents : measure === "costs" ? row.costsCents : row.profitCents;
  return c / 100;
}

const MEASURE_NAME = { revenue: "Revenue", costs: "Costs", profit: "Profit", margin: "Margin" };
const DATA_NAME = { monthly: "Monthly", jobs: "Jobs", clients: "Clients", job_types: "Job types" };

function rangeText(from, to) {
  if (from && to) return from === to ? longMonth(from) : `${longMonth(from)} to ${longMonth(to)}`;
  if (from) return `${longMonth(from)} onward`;
  return `up to ${longMonth(to)}`;
}

function defaultTitle(spec) {
  const m = MEASURE_NAME[spec.measure];
  if (spec.data === "monthly") return `Monthly ${m.toLowerCase()}`;
  const by = spec.data === "jobs" ? "job" : spec.data === "clients" ? "client" : "job type";
  return `${m} by ${by}`;
}

// jobs: the job summaries AIChat already has (built in useContractorData in src/App.js).
// Returns { ok:false } or { ok:true, spec, title, measure, unit, rows, notes, ariaLabel, orientation }.
// Each row: { key, label, fullLabel, value (number or null), revenueCents, costsCents, profitCents, margin }.
export function buildChart(rawSpec, jobs) {
  const spec = parseChartSpec(rawSpec);
  if (!spec || !Array.isArray(jobs)) return { ok: false };
  const { from, to, measure } = spec;
  const inRange = (ym) => ym !== null && (!from || ym >= from) && (!to || ym <= to);
  const ranged = !!(from || to);
  const notes = [];
  let rows = [];
  let undatedCostCents = 0;
  let undatedRevCents = 0;

  if (spec.data === "monthly") {
    const tally = bucketByMonth(jobs, inRange);
    const buckets = tally.buckets;
    undatedCostCents = tally.undatedCostCents;
    undatedRevCents = tally.undatedRevCents;
    if (buckets.size === 0) return { ok: false };
    const keys = [...buckets.keys()].sort();
    let lo = from ? monthIndex(from) : monthIndex(keys[0]);
    let hi = to ? monthIndex(to) : monthIndex(keys[keys.length - 1]);
    if (hi - lo + 1 > MAX_MONTHS) {
      if (from && to) return { ok: false };
      if (!from) lo = hi - MAX_MONTHS + 1; else hi = lo + MAX_MONTHS - 1;
      notes.push(from ? `Showing the first ${MAX_MONTHS} months.` : `Showing the latest ${MAX_MONTHS} months.`);
    }
    const yearsSeen = new Set();
    for (let i = lo; i <= hi; i++) yearsSeen.add(Math.floor(i / 12));
    for (let i = lo; i <= hi; i++) {
      const ym = monthFromIndex(i);
      const b = buckets.get(ym) || { rev: 0, cost: 0 };
      const label = yearsSeen.size > 1 ? `${shortMonth(ym)} '${ym.slice(2, 4)}` : shortMonth(ym);
      rows.push(makeRow(ym, label, longMonth(ym), b.rev, b.cost));
    }
    if (undatedCostCents !== 0) notes.push(`Leaves out ${formatMoney(undatedCostCents / 100)} of costs that have no date.`);
    if (undatedRevCents !== 0) notes.push(`Leaves out ${formatMoney(undatedRevCents / 100)} of revenue that has no date.`);
  } else {
    const groups = new Map(); // name -> {rev, cost}
    for (const job of jobs) {
      if (!job || typeof job !== "object") continue;
      const name = spec.data === "jobs" ? (job.name || "Unnamed job")
        : spec.data === "clients" ? (job.clientName || "No client")
        : (job.type || "No job type");
      let rev = 0;
      let cost = 0;
      for (const e of jobEvents(job)) {
        if (e.ym === null) {
          if (ranged) { if (e.kind === "cost") undatedCostCents += e.cents; else undatedRevCents += e.cents; continue; }
        } else if (!inRange(e.ym)) continue;
        if (e.kind === "rev") rev += e.cents; else cost += e.cents;
      }
      if (rev === 0 && cost === 0) continue;
      const key = spec.data === "jobs" ? `${job.id ?? name}` : name;
      const g = groups.get(key) || { name, rev: 0, cost: 0 };
      g.rev += rev;
      g.cost += cost;
      groups.set(key, g);
    }
    rows = [...groups.entries()].map(([key, g]) => makeRow(key, g.name, g.name, g.rev, g.cost));
    if (rows.length === 0) return { ok: false };
    const withValue = rows.filter((r) => valueOf(r, measure) !== null);
    const noRev = rows.length - withValue.length;
    const top = spec.top || DEFAULT_TOP;
    // Sort by the measure, biggest first; ties by name, then key, so order never jumps around.
    withValue.sort((a, b) => valueOf(b, measure) - valueOf(a, measure)
      || a.label.localeCompare(b.label) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    if (withValue.length === 0) return { ok: false };
    rows = withValue.slice(0, top);
    if (withValue.length > top) notes.push(`Top ${top} of ${withValue.length}.`);
    if (noRev > 0) notes.push(`Margin isn't shown for ${noRev} ${noRev === 1 ? "item" : "items"} without positive revenue.`);
    if (ranged) {
      notes.push(`Counts invoices, expenses and labor dated ${rangeText(from, to)}.`);
      if (undatedCostCents !== 0) notes.push(`Leaves out ${formatMoney(undatedCostCents / 100)} of costs that have no date.`);
      if (undatedRevCents !== 0) notes.push(`Leaves out ${formatMoney(undatedRevCents / 100)} of revenue that has no date.`);
    } else {
      notes.push("Counts all dates.");
    }
  }

  if (spec.data === "monthly" && measure === "margin") {
    const noRev = rows.filter((r) => r.margin === null).length;
    if (noRev > 0) notes.push(`Margin isn't shown for ${noRev} ${noRev === 1 ? "month" : "months"} without positive revenue.`);
  }

  const out = rows.map((r) => ({ ...r, value: valueOf(r, measure) }));
  if (out.every((r) => r.value === null)) return { ok: false };
  const title = spec.title || defaultTitle(spec);
  const unit = measure === "margin" ? "percent" : "money";
  const fmt = (v) => (unit === "percent" ? formatPercent(v) : formatMoney(v));
  const known = out.filter((r) => r.value !== null);
  const hi = known.reduce((a, b) => (b.value > a.value ? b : a));
  const lo = known.reduce((a, b) => (b.value < a.value ? b : a));
  const kind = spec.type === "line" ? "Line chart" : "Bar chart";
  const ariaLabel = `${kind}: ${title}. ${out.length} ${out.length === 1 ? "point" : "points"}. `
    + `Highest ${hi.fullLabel}, ${fmt(hi.value)}. Lowest ${lo.fullLabel}, ${fmt(lo.value)}.`;

  return {
    ok: true, spec, title, measure, unit, rows: out, notes, ariaLabel,
    orientation: spec.data === "monthly" ? "columns" : "rows",
    labelHeader: spec.data === "monthly" ? "Month" : spec.data === "jobs" ? "Job" : spec.data === "clients" ? "Client" : "Job type",
    dataName: DATA_NAME[spec.data],
  };
}

// Table view of the same rows: all four measures so the owner can check the picture.
export function chartTable(chart) {
  const header = [chart.labelHeader, "Revenue", "Costs", "Profit", "Margin"];
  // In each money column, if any value has cents, every value shows 2 decimals.
  const col = (key) => {
    const withCents = chart.rows.some((r) => r[key] % 100 !== 0);
    return (r) => formatMoney(r[key] / 100, withCents);
  };
  const rev = col("revenueCents");
  const cost = col("costsCents");
  const profit = col("profitCents");
  const rows = chart.rows.map((r) => [
    r.fullLabel,
    rev(r),
    cost(r),
    profit(r),
    r.margin === null ? "-" : formatPercent(r.margin),
  ]);
  return { type: "table", header, rows };
}
